'use client';

/**
 * GrowthMissions - Checklist panel for partner milestones
 */

import React from 'react';
import { Sparkles, CheckCircle2, Circle } from 'lucide-react';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/glass-card';
import type { MissionWithProgress } from '../types';

interface GrowthMissionsProps {
  missions: MissionWithProgress[];
}

// Reward type to badge style mapping
const rewardStyles: Record<string, { bg: string; text: string }> = {
  unlock: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-400',
  },
  bonus: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
  },
  badge: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
  },
  rate: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
  },
};

export function GrowthMissions({ missions }: GrowthMissionsProps) {
  const completedCount = missions.filter((m) => m.is_completed).length;
  const totalCount = missions.length;

  return (
    <GlassCard className="h-fit">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-base">Growth Missions</CardTitle>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedCount}/{totalCount} Done
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-3">
          {missions.map((mission) => {
            const rewardStyle = rewardStyles[mission.reward_type] || rewardStyles.badge;

            return (
              <div
                key={mission.id}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  mission.is_completed
                    ? 'bg-emerald-50 dark:bg-emerald-900/10'
                    : 'bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Checkbox */}
                  {mission.is_completed ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-300 dark:text-gray-600 shrink-0" />
                  )}

                  {/* Mission Name */}
                  <span
                    className={`text-sm font-medium ${
                      mission.is_completed
                        ? 'text-gray-500 dark:text-gray-400 line-through'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {mission.display_name}
                  </span>
                </div>

                {/* Reward Badge */}
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${rewardStyle.bg} ${rewardStyle.text}`}
                >
                  {mission.reward_value}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </GlassCard>
  );
}
